import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getPublicQuote, publicQuotePdfUrl, markQuoteSeen } from "../api/quotes";
import { formatUSD } from "../utils/money";
import { formatEasternDateTime } from "../utils/dates";
import SilentBoundary from "../components/common/SilentBoundary";
import QuoteExtras from "../components/pay/QuoteExtras";

// Format a plain "YYYY-MM-DD" without letting it round-trip through UTC —
// new Date("2026-10-16") is UTC midnight and renders as the 15th out west.
function formatDueDate(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

// Customer-facing, no login: shows a quote and a Square Pay button. This is the
// entire customer surface in the rebuilt workflow — paying confirms the job.
export default function PayQuote() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const justPaid = params.get("paid") === "1";

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const pollRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const q = await getPublicQuote(token);
      setQuote(q);
      return q;
    } catch (e) {
      setError(e.status === 404 ? "This quote link is no longer available." : e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Once per visit, after the quote has loaded: lets you know they're looking.
  const seenRef = useRef(false);
  useEffect(() => {
    if (!quote || seenRef.current) return;
    seenRef.current = true;
    markQuoteSeen(token);
  }, [quote, token]);

  // Coming back from Square, the webhook may take a moment. Poll briefly until
  // the payment shows as received.
  useEffect(() => {
    if (!justPaid || !quote || quote.payment_status === "paid") return;
    if (pollRef.current >= 10) return;
    const t = setTimeout(async () => {
      pollRef.current += 1;
      await load();
    }, 3000);
    return () => clearTimeout(t);
  }, [justPaid, quote, load]);

  const isPaid = quote?.payment_status === "paid";
  const amountDue = quote?.amount_due_cents || 0;
  // Each option carries its own Square link (deposit / full). Older quotes only
  // have the single legacy payment_url, so fall back to that.
  const options =
    quote?.payment_options?.length
      ? quote.payment_options
      : quote?.payment_url
      ? [{ kind: "full", amount_cents: amountDue, url: quote.payment_url }]
      : [];
  const balance = quote?.balance_cents ?? 0;
  // Paid a deposit but still owing? That's still payable — the options list
  // will hold the balance link.
  const canPay = options.length > 0 && (!isPaid || balance > 0);
  const dueDate = formatDueDate(quote?.balance_due_date);

  return (
    <div className="min-h-screen bg-birdie-beige py-8 px-4">
      <div className="mx-auto max-w-xl">
        <div className="text-center mb-6">
          <img
            src="/birdie-bus-logo.png"
            alt="Birdie Bus"
            className="h-16 w-auto mx-auto"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>

        {loading ? (
          <Card>
            <div className="py-10 text-center text-birdie-charcoal/60">Loading your quote…</div>
          </Card>
        ) : error ? (
          <Card>
            <div className="py-10 text-center">
              <div className="text-3xl mb-2">😕</div>
              <p className="text-birdie-charcoal/80">{error}</p>
              <p className="text-sm text-birdie-charcoal/50 mt-2">
                If you think this is a mistake, reply to your quote email and we'll help.
              </p>
            </div>
          </Card>
        ) : (
          <>
            {isPaid ? (
              <Banner tone="green">
                <span className="text-lg">✅</span> Payment received — your event is confirmed!
                {balance > 0 && (
                  <div className="font-normal mt-1">
                    Remaining balance of {formatUSD(balance)} is due
                    {dueDate ? ` by ${dueDate}` : " the day before your event"}.
                  </div>
                )}
              </Banner>
            ) : justPaid ? (
              <Banner tone="mustard">
                <span className="text-lg">⏳</span> Finalizing your payment… this page will update
                automatically.
              </Banner>
            ) : null}

            <img
              src="/bus-banner.jpg"
              alt="The Birdie Bus mobile golf simulator"
              className="w-full rounded-2xl mb-4 shadow-sm"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            <Card>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-xs text-birdie-charcoal/50">
                    {quote.quote_number}
                  </div>
                  <h1 className="font-heading text-2xl text-birdie-green mt-0.5">
                    {quote.title || "Your Birdie Bus Quote"}
                  </h1>
                  {quote.customer_name && (
                    <div className="text-sm text-birdie-charcoal/70 mt-1">
                      Prepared for {quote.customer_name}
                    </div>
                  )}
                </div>
              </div>

              {(quote.event_date || quote.event_location) && (
                <div className="mt-4 rounded-xl bg-birdie-cream border border-birdie-green/15 p-3 text-sm text-birdie-charcoal/80 space-y-1">
                  {quote.event_date && (
                    <div>📅 {formatEasternDateTime(quote.event_date, { timeZoneName: "short" })}</div>
                  )}
                  {quote.event_location && <div>📍 {quote.event_location}</div>}
                </div>
              )}

              <div className="mt-5 divide-y divide-birdie-green/10">
                {(quote.line_items || []).map((li, i) => (
                  <div key={i} className="flex justify-between gap-3 py-2 text-sm">
                    <div className="text-birdie-charcoal">
                      {li.description}
                      {Number(li.quantity) !== 1 && (
                        <span className="text-birdie-charcoal/50">
                          {" "}
                          × {Number(li.quantity)}
                          {li.unit_label ? ` ${li.unit_label}` : ""}
                        </span>
                      )}
                    </div>
                    <div className="text-birdie-charcoal/80 whitespace-nowrap">
                      {formatUSD(li.amount_cents)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-birdie-green/15 space-y-1 text-sm">
                <Row label="Subtotal" value={formatUSD(quote.subtotal_cents)} />
                {quote.discount_amount_cents > 0 && (
                  <Row
                    label={quote.discount_reason ? `Discount (${quote.discount_reason})` : "Discount"}
                    value={`-${formatUSD(quote.discount_amount_cents)}`}
                  />
                )}
                {quote.tax_cents > 0 && (
                  <Row
                    label={
                      quote.tax_county
                        ? `Estimated sales tax (${quote.tax_county})`
                        : "Estimated sales tax"
                    }
                    value={formatUSD(quote.tax_cents)}
                  />
                )}
                {quote.credit_applied_cents > 0 && (
                  <Row
                    label={quote.credit_label || "Credit"}
                    value={`-${formatUSD(quote.credit_applied_cents)}`}
                  />
                )}
                <div className="flex justify-between items-center pt-2">
                  <span className="font-bold text-birdie-charcoal">Total</span>
                  <span className="text-xl font-bold text-birdie-green">
                    {formatUSD(quote.total_cents)}
                  </span>
                </div>
                {quote.deposit_cents > 0 && !isPaid && (
                  <div className="flex justify-between text-xs text-birdie-charcoal/60">
                    <span>Due now (deposit)</span>
                    <span>{formatUSD(amountDue)}</span>
                  </div>
                )}
              </div>

              {quote.notes && (
                <p className="mt-4 text-sm text-birdie-charcoal/70 whitespace-pre-line">
                  {quote.notes}
                </p>
              )}

              <div className="mt-6 space-y-3">
                {canPay &&
                  options.map((opt, i) => {
                    const isDeposit = opt.kind === "deposit";
                    const isBalance = opt.kind === "balance";
                    const pct =
                      isDeposit && quote.total_cents
                        ? Math.round((opt.amount_cents / quote.total_cents) * 100)
                        : null;
                    // The full amount is the headline action; deposit is secondary.
                    const primary = !isDeposit || isBalance;
                    return (
                      <a
                        key={opt.kind || i}
                        href={opt.url}
                        className={`block w-full text-center font-bold rounded-xl px-6 py-3.5 transition ${
                          primary
                            ? "bg-birdie-green text-white hover:bg-birdie-green-dark"
                            : "border-2 border-birdie-green/30 text-birdie-green hover:bg-birdie-cream"
                        }`}
                      >
                        {isBalance
                          ? `Pay remaining balance — ${formatUSD(opt.amount_cents)}`
                          : isDeposit
                          ? `Pay ${pct}% deposit — ${formatUSD(opt.amount_cents)}`
                          : `Pay in full — ${formatUSD(opt.amount_cents)}`}
                      </a>
                    );
                  })}
                {canPay && options.length > 1 && !isPaid && (
                  <p className="text-center text-xs text-birdie-charcoal/50">
                    A deposit reserves your date; the balance is due
                    {dueDate ? ` by ${dueDate}` : " the day before your event"}.
                  </p>
                )}
                {!isPaid && !canPay && (
                  <div className="text-center text-sm text-birdie-charcoal/60 bg-birdie-cream rounded-xl p-3">
                    A payment link isn't ready yet. Reply to your quote email and we'll send one over.
                  </div>
                )}
                <a
                  href={publicQuotePdfUrl(token)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center border border-birdie-green/25 bg-white text-birdie-charcoal font-medium rounded-xl px-6 py-3 hover:bg-birdie-cream transition"
                >
                  View / download PDF
                </a>
              </div>

              {canPay && (
                <p className="mt-3 text-center text-xs text-birdie-charcoal/45">
                  Payments are processed securely by Square.
                </p>
              )}
            </Card>

            {/* Extras, reviews and FAQ load separately; if they fail, they
                just don't show, and the quote above is unaffected. */}
            <SilentBoundary token={token} where="/pay (extras)">
              <QuoteExtras token={token} quote={quote} />
            </SilentBoundary>
          </>
        )}

        <p className="text-center text-xs text-birdie-charcoal/45 mt-6">
          Birdie Bus · Mobile Golf Simulator · Southwest Florida
        </p>
      </div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="bg-white border border-birdie-green/15 rounded-2xl p-5 sm:p-6 shadow-sm">
      {children}
    </div>
  );
}

function Banner({ tone, children }) {
  const tones = {
    green: "border-birdie-green/25 bg-birdie-green/10 text-birdie-green",
    mustard: "border-birdie-mustard/40 bg-birdie-mustard/10 text-birdie-charcoal",
  };
  return (
    <div className={`rounded-xl border p-3 mb-4 text-sm font-medium text-center ${tones[tone]}`}>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-birdie-charcoal/70">{label}</span>
      <span className="text-birdie-charcoal">{value}</span>
    </div>
  );
}
